import { useState, useEffect } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/cms/ui/dialog';
import LureoButton from '@/common/LureoButton';
import { TextButton } from '@/components/ui/text-button';
import { ArrowClockwiseIcon, CheckIcon } from '@phosphor-icons/react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { toast } from 'sonner';
import { OrgInstance } from '@/types/orgTypes';
import { Segmented } from '@/components/ui/segmented';
import { Card } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';

export type PlanTier = 'starter' | 'creator' | 'pro';
export type BillingTerm = 'monthly' | 'yearly';

interface PlanSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onVoucherClick: () => void;
}

export function PlanSelectionModal({
  open,
  onClose,
  onVoucherClick
}: PlanSelectionModalProps) {
  const services = useServices();
  const { user } = useAuthUser();
  const [orgInstance, setOrgInstance] = useState<OrgInstance | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<BillingTerm>('monthly');
  const [loading, setLoading] = useState<PlanTier | null>(null);

  // The org is read when the modal opens, not while it sits closed
  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;
    services.organization
      .fetchOrgInstanceByUserId(user.id)
      .then(org => {
        if (!cancelled) setOrgInstance(org);
      })
      .catch(error => console.error('Error loading the organization:', error));
    return () => {
      cancelled = true;
    };
  }, [open, user?.id, services.organization]);

  const handleSelectPlan = async (tier: PlanTier) => {
    // Starter plan - just close modal
    if (tier === 'starter') {
      onClose();
      return;
    }

    if (!orgInstance?.orgInstanceId) {
      toast.error('Organization not found. Please try again.');
      return;
    }

    // Creator/Pro plans - start checkout
    setLoading(tier);
    try {
      // Convert 'monthly' to 'month', 'yearly' to 'year'
      const term = selectedTerm === 'monthly' ? 'month' : 'year';

      const { checkoutUrl } = await services.billing.startCheckout({
        tier,
        term,
        orgInstanceId: orgInstance.orgInstanceId
      });

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      // Error is already handled by billingService (toast shown)
      console.error('Checkout error:', error);
      setLoading(null);
    }
  };

  const plans = {
    starter: {
      name: 'Starter',
      price: 'Free',
      yearlyPrice: 'Free',
      description: 'Perfect for trying out Lureo',
      features: [
        // 'No of experiences: 2',
        'Version history: No',
        'Published experiences: 0',
        'File upload size: 10MB',
        'Watermark: Yes'
      ]
    },
    creator: {
      name: 'Creator',
      monthlyPrice: '€19.00',
      yearlyPrice: '€15.20',
      description: 'Most popular for content creators',
      features: [
        // 'No of experiences: Unlimited',
        'Version history: Yes',
        'Published experiences: 2',
        'File upload size: 25MB',
        'Watermark: No'
      ],
      mostPopular: true
    },
    pro: {
      name: 'Pro',
      monthlyPrice: '€49.00',
      yearlyPrice: '€39.20',
      description: 'For professional teams',
      features: [
        // 'No of experiences: Unlimited',
        'Version history: Yes',
        'Published experiences: 20',
        'File upload size: 50MB',
        'Watermark: No'
      ]
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* Three plan cards side by side need more room than the dialog widths give */}
      <DialogContent width="xl">
        <DialogHeader>
          <DialogTitle>Choose Your Plan</DialogTitle>
          <DialogDescription>
            Select the perfect plan for your storytelling needs
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* Term Toggle */}
          <div className="flex justify-center">
            <Segmented
              variant="pills"
              label="Billing"
              options={[
                { id: 'monthly', label: 'Monthly' },
                { id: 'yearly', label: 'Yearly' },
              ]}
              value={selectedTerm}
              onChange={setSelectedTerm}
            />
          </div>

          {/* Plan Cards Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-2">
            {/* Starter Card */}
            <Card padding="lg">
              <div className="mb-4">
                <CardTitle as="h3">{plans.starter.name}</CardTitle>
                <p className="text-lureo-14 text-lureo-ink-subtle leading-5">{plans.starter.description}</p>
              </div>

              <div className="mb-6">
                <div className="text-lureo-36 font-bold text-lureo-ink-strong leading-10">
                  {plans.starter.price}
                </div>
                <p className="text-lureo-14 text-lureo-ink-subtle mt-1 leading-5">Save 20% on yearly plan</p>
              </div>

              <LureoButton
                onClick={() => handleSelectPlan('starter')}
                variant="secondary"
                suppressTooltip
                className="w-full mb-6 h-12 rounded-lureo-radius-button-pill px-6"
              >
                <ArrowClockwiseIcon className="h-4 w-4" />
                Get started
              </LureoButton>

              <ul className="space-y-3 py-8">
                {plans.starter.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-lureo-14 leading-5">
                    <CheckIcon className="w-5 h-5 text-lureo-sky-ink shrink-0 mt-0.5" />
                    <span className="text-lureo-ink">{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Creator Card - Most Popular */}
            <div className="rounded-lureo-radius-lg p-6 sm:p-8 border-lureo-border-width-medium border-lureo-plan-line bg-lureo-emphasis-strong relative">
              <div className="absolute top-4 right-4">
                <LureoButton
                  variant="secondary"
                  suppressTooltip
                  className="h-8 rounded-lureo-radius-button-pill px-4 text-lureo-12 uppercase pointer-events-none leading-4"
                >
                  Most Popular
                </LureoButton>
              </div>

              <div className="mb-4">
                <CardTitle as="h3" tone="emphasis">{plans.creator.name}</CardTitle>
                <p className="text-lureo-14 text-lureo-on-emphasis-subtle leading-5">{plans.creator.description}</p>
              </div>

              <div className="mb-6">
                <div className="text-lureo-36 font-bold text-lureo-on-emphasis leading-10">
                  {selectedTerm === 'monthly' ? plans.creator.monthlyPrice : plans.creator.yearlyPrice}
                  <span className="text-lureo-18 font-normal text-lureo-on-emphasis-subtle leading-7">/month</span>
                </div>
                <p className="text-lureo-14 text-lureo-on-emphasis-subtlest mt-1 leading-5">Save 20% on yearly plan</p>
              </div>

              <LureoButton
                onClick={() => handleSelectPlan('creator')}
                variant="secondary"
                suppressTooltip
                className="w-full mb-2 h-12 rounded-lureo-radius-button-pill px-6"
                disabled={loading !== null}
              >
                {loading === 'creator' ? (
                  <>
                    <LoadingSpinner size="sm" tone="on-cta" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ArrowClockwiseIcon className="h-4 w-4" />
                    Get started
                  </>
                )}
              </LureoButton>

              <TextButton tone="feature" onClick={onVoucherClick} className="w-full justify-center">
                Have a voucher?
              </TextButton>

              <ul className="space-y-3 mt-6">
                {plans.creator.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-lureo-14 leading-5">
                    <CheckIcon className="w-5 h-5 text-lureo-plan-link shrink-0 mt-0.5" />
                    <span className="text-lureo-on-emphasis">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro Card */}
            <Card padding="lg">
              <div className="mb-4">
                <CardTitle as="h3">{plans.pro.name}</CardTitle>
                <p className="text-lureo-14 text-lureo-ink-subtle leading-5">{plans.pro.description}</p>
              </div>

              <div className="mb-6">
                <div className="text-lureo-36 font-bold text-lureo-ink-strong leading-10">
                  {selectedTerm === 'monthly' ? plans.pro.monthlyPrice : plans.pro.yearlyPrice}
                  <span className="text-lureo-18 font-normal text-lureo-ink-subtle leading-7">/month</span>
                </div>
                <p className="text-lureo-14 text-lureo-ink-subtle mt-1 leading-5">Save 20% on yearly plan</p>
              </div>

              <LureoButton
                onClick={() => handleSelectPlan('pro')}
                variant="secondary"
                suppressTooltip
                className="w-full mb-6 h-12 rounded-lureo-radius-button-pill px-6"
                disabled={loading !== null}
              >
                {loading === 'pro' ? (
                  <>
                    <LoadingSpinner size="sm" tone="on-cta" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ArrowClockwiseIcon className="h-4 w-4" />
                    Get started
                  </>
                )}
              </LureoButton>

              <ul className="space-y-3 py-8">
                {plans.pro.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-lureo-14 leading-5">
                    <CheckIcon className="w-5 h-5 text-lureo-sky-ink shrink-0 mt-0.5" />
                    <span className="text-lureo-ink">{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Footer Note */}
          <p className="text-center text-lureo-14 text-lureo-ink-subtle leading-5">
            You can change or cancel your plan at any time
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

